import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DragEvent, JSX, ReactNode } from 'react';
import {
  COULEURS_DE_FICHE_PERSO_DEFAUT,
  FICHE_PERSO_LIBELLE,
  HAUTEUR_DE_FICHE_PERSO_DEFAUT,
  VUE_HTML_TAILLE_MAX_OCTETS,
  contexteMcpDeFichePerso,
  couleursDeFichePerso,
  hauteurDeFichePerso,
  idDeFichePerso,
  lirePageDeFichePerso,
  promptDeFichePerso,
} from '@taki/data-model';
import type { ChampDeFichePerso, CouleursDeFichePerso, HauteurDeFichePerso, LectureDePageDeFichePerso } from '@taki/data-model';
import { Button, Modal, SettingsMenu, VueHtmlIsolee, cn } from '@taki/ui';
import { useAppRuntime } from './runtime.js';
import type { DonneesDeVueHtml } from './vues-de-fiche-donnees.js';
import type { ActionsDeVueDeFiche } from './actions-de-vue-de-fiche.js';
import type { LectureDeLaFiche } from './vues-de-fiche.js';

/**
 * L'ATELIER D'UNE FICHE PERSONNALISEE (chantier `fiche-canva-v2`,
 * `docs/socle/fiche-suivi.md` section 3) : ce que la vue « Fiche personnalisée »
 * montre tant qu'aucun HTML n'est publie, et ce que la fenetre « Éditer la vue
 * Canvas » montre une fois publie. UN SEUL composant pour les deux.
 *
 * Trois blocs plats, empiles, separes par des filets, sans carte ni decor :
 * « Prompt IA » (les choix des couleurs et de la hauteur, puis le prompt a copier pour son assistant), « Fichier HTML » (le deposer, le
 * voir tout de suite dans le cadre ferme, le publier) et « Agent connecté »
 * (le contexte a coller a un agent deja branche au MCP). Le blocage du MCP ne
 * bloque ni le prompt ni le depot.
 *
 * LE DROIT DE DEPOSER n'est pas juge ici : l'ecran ne monte l'atelier que pour
 * qui peut (`ContexteDeFichePerso.peutDeposer`). La vraie barriere est la
 * callable du serveur (`deposerVueHtml`), qui refait tous les controles.
 */

/** Le fichier choisi, tenu au-dessus de l'atelier tant que la fiche reste ouverte. */
export interface DepotDeFichePerso {
  nom: string;
  html: string;
}

/** Ce que l'on publie : les arguments de `vue_proposer`, moins ce que l'ecran sait deja (app, porte, fiche, id). */
export interface PageDeFichePerso {
  label: string;
  fields: string[];
  links: string[];
  html: string;
}

/**
 * CE QUE L'ECRAN PRETE A L'ATELIER, pour la porte active (`null` hors d'une porte
 * qui active la fiche personnalisee). Pose par `EcranDeclare`.
 */
export interface ContexteDeFichePerso {
  app: { id: string; libelle: string };
  /** L'ecran de la porte. */
  screenId: string;
  orgId: string;
  porte: { id: string; libelle: string; ficheSingulier?: string | undefined };
  /** Les champs que la page peut lire (`champsDeFichePerso`, le miroir du serveur). */
  champs: readonly ChampDeFichePerso[];
  /** Les liens de la porte : id et nom de la porte liee. */
  liens: readonly { id: string; label: string }[];
  /** Proprietaire ou administrateur actif, hors de l'org `master`, et un SDK qui sait deposer. */
  peutDeposer: boolean;
  /** La vue deja publiee sur cette porte, telle que la declaration de l'org la porte (`null` : pas encore). */
  publiee: { label: string; version: number; html: string; champs: readonly string[]; liens?: readonly string[] | undefined } | null;
  /** Le fichier non publie de CETTE fiche sur CETTE porte. */
  depot: DepotDeFichePerso | null;
  poserDepot(depot: DepotDeFichePerso | null): void;
  /** Publie la page ; ne rend qu'une fois la vue arrivee dans la declaration de l'org. Rejette avec la raison. */
  deposer(page: PageDeFichePerso): Promise<void>;
}

/** Ce que l'atelier lit de la fiche ouverte. */
export interface EntreeDeLAtelier {
  fichePerso: ContexteDeFichePerso;
  ficheId: string;
  lecture: LectureDeLaFiche | null;
  /** Les donnees d'une vue HTML pour la fiche ouverte (l'apercu les reçoit reduites aux champs lus). */
  donneesDe(vue: { champs: readonly string[]; liens?: readonly string[] | undefined }, lecture: LectureDeLaFiche | null): DonneesDeVueHtml | null;
}

/** L'adresse ou se regle l'IA de l'org et les connexions MCP de la personne (hub Parametres, onglet Conversations). */
export const ADRESSE_DES_PARAMETRES_IA = '/settings?t=conversations';

/* ------------------------------------------------------------------ */
/* L'etat de l'atelier                                                 */
/* ------------------------------------------------------------------ */

/** Les raisons d'un bloc « Agent connecté » grise, dans l'ordre ou on les leve. */
export const RAISON_IA_ETEINTE = 'IA désactivée';
export const RAISON_SANS_CONNEXION = 'Aucune connexion active';
export const RAISON_LECTURE_SEULE = 'Connexion en lecture seule';
const RAISON_VERIFICATION = 'Vérification en cours';

/** Une connexion MCP, telle que l'atelier la juge. */
export interface ConnexionLue {
  revokedAt: Date | null;
  portee?: string | undefined;
}

/**
 * LE BLOC « AGENT CONNECTE » EST-IL UTILISABLE ? Oui si l'IA de l'org est
 * allumee ET que la personne a une connexion MCP a elle, non revoquee, de
 * portee reduite ou complete (portee absente = reduite). Sinon la raison, en un
 * mot. PURE, exportee pour un test.
 */
export function etatDeLAgent(
  iaAllumee: boolean | null,
  connexions: readonly ConnexionLue[] | null,
): { pret: true } | { pret: false; raison: string } {
  if (iaAllumee === null || connexions === null) return { pret: false, raison: RAISON_VERIFICATION };
  if (!iaAllumee) return { pret: false, raison: RAISON_IA_ETEINTE };
  const actives = connexions.filter((connexion) => connexion.revokedAt === null);
  if (actives.length === 0) return { pret: false, raison: RAISON_SANS_CONNEXION };
  if (!actives.some((connexion) => (connexion.portee ?? 'reduite') !== 'lecture')) {
    return { pret: false, raison: RAISON_LECTURE_SEULE };
  }
  return { pret: true };
}

/** L'IA de l'org et les connexions MCP de la personne, lues sur le SDK. */
function useEtatDeLAgent(): { pret: true } | { pret: false; raison: string } {
  const { sdk } = useAppRuntime();
  const [iaAllumee, setIaAllumee] = useState<boolean | null>(null);
  const [connexions, setConnexions] = useState<readonly ConnexionLue[] | null>(null);
  useEffect(() => {
    const reglages = (sdk as unknown as {
      iaSettings?: {
        observer(onValue: (reglages: { enabled: boolean }) => void, onError?: (erreur: unknown) => void): () => void;
        connections(): Promise<readonly ConnexionLue[]>;
      };
    }).iaSettings;
    if (reglages === undefined) {
      setIaAllumee(false);
      setConnexions([]);
      return;
    }
    let vivant = true;
    const arreter = reglages.observer(
      (valeur) => { if (vivant) setIaAllumee(valeur.enabled); },
      () => { if (vivant) setIaAllumee(false); },
    );
    reglages.connections().then(
      (liste) => { if (vivant) setConnexions(liste); },
      () => { if (vivant) setConnexions([]); },
    );
    return () => {
      vivant = false;
      arreter();
    };
  }, [sdk]);
  return etatDeLAgent(iaAllumee, connexions);
}

/**
 * LA COQUE D'UN BOUTON GRISE (G16, forme 2) : le bouton reste `disabled`, c'est ce
 * `span` parent qui recoit le survol, porte la raison en `title` et le curseur
 * `not-allowed`. Sans raison, le bouton est rendu tel quel.
 */
function CoqueDeRaison({ raison, children }: { raison: string | undefined; children: ReactNode }): JSX.Element {
  if (raison === undefined) return <>{children}</>;
  return <span title={raison} className="inline-flex cursor-not-allowed">{children}</span>;
}

/** Copie un texte ; rend vrai si le presse-papiers l'a pris. */
async function copier(texte: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(texte);
    return true;
  } catch {
    return false;
  }
}

/** « Copier... », puis « Copié » le temps de le lire. */
function useCopie(): { copie: boolean; copier(texte: string): void } {
  const [copie, setCopie] = useState(false);
  const minuteur = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(minuteur.current), []);
  return {
    copie,
    copier: (texte) => {
      void copier(texte).then((pris) => {
        if (!pris) return;
        setCopie(true);
        window.clearTimeout(minuteur.current);
        minuteur.current = window.setTimeout(() => setCopie(false), 1500);
      });
    },
  };
}

/** Ce que le hook rend : tout ce que le corps affiche, et le geste de publier. */
export interface EtatDeLAtelier {
  depot: DepotDeFichePerso | null;
  /** La lecture locale du fichier (`lirePageDeFichePerso`), `null` sans fichier. */
  page: LectureDePageDeFichePerso | null;
  /** Le fichier est lisible en entier et publiable. */
  valide: boolean;
  enCours: boolean;
  /** L'echec du dernier geste (lecture ou publication), sous le bloc du fichier. */
  erreur: string | null;
  /** Pourquoi « Publier » est grise, ou `undefined` quand il se peut. */
  raisonDeRefus: string | undefined;
  choisir(fichier: File): Promise<void>;
  publier(): Promise<void>;
}

/** Le libelle du bouton « Publier » : il dit l'appel en cours. */
export const libelleDePublication = (enCours: boolean): string => (enCours ? 'Publication…' : 'Publier');

/**
 * L'ETAT DE L'ATELIER : le fichier choisi (tenu par l'ecran, donc garde quand la
 * fenetre se ferme), sa lecture locale, et la publication. `onPublie` est appele
 * quand la vue publiee est arrivee (la fenetre se ferme).
 */
export function useAtelierDeFichePerso(fichePerso: ContexteDeFichePerso, onPublie?: () => void): EtatDeLAtelier {
  const { depot, poserDepot } = fichePerso;
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const vivant = useRef(true);
  useEffect(() => {
    vivant.current = true;
    return () => { vivant.current = false; };
  }, []);
  const connus = useMemo(
    () => ({ champs: fichePerso.champs.map((champ) => champ.name), liens: fichePerso.liens.map((lien) => lien.id) }),
    [fichePerso.champs, fichePerso.liens],
  );
  const page = useMemo(() => (depot === null ? null : lirePageDeFichePerso(depot.html, connus)), [depot, connus]);
  const valide = page?.ok === true;

  const choisir = useCallback(
    async (fichier: File): Promise<void> => {
      setErreur(null);
      if (fichier.size > VUE_HTML_TAILLE_MAX_OCTETS * 8) {
        /* Un fichier sans commune mesure n'est pas lu : la lecture le refuserait de toute facon. */
        poserDepot({ nom: fichier.name, html: '' });
        setErreur(`Le fichier pèse ${Math.ceil(fichier.size / 1024)} Ko, ${Math.round(VUE_HTML_TAILLE_MAX_OCTETS / 1024)} Ko au plus.`);
        return;
      }
      try {
        poserDepot({ nom: fichier.name, html: await fichier.text() });
      } catch {
        setErreur('Le fichier n’a pas pu être lu.');
      }
    },
    [poserDepot],
  );
  const publier = useCallback(async (): Promise<void> => {
    if (depot === null || page === null || !page.ok || enCours) return;
    setEnCours(true);
    setErreur(null);
    try {
      await fichePerso.deposer({
        label: fichePerso.publiee?.label ?? FICHE_PERSO_LIBELLE,
        fields: page.champs,
        links: page.liens,
        html: depot.html,
      });
      poserDepot(null);
      onPublie?.();
    } catch (cause) {
      if (vivant.current) setErreur(cause instanceof Error ? cause.message : String(cause));
    } finally {
      if (vivant.current) setEnCours(false);
    }
  }, [depot, page, enCours, fichePerso, poserDepot, onPublie]);

  const raisonDeRefus = enCours
    ? 'Publication en cours'
    : valide
      ? undefined
      : 'Choisir d’abord un fichier valide';
  return { depot, page, valide, enCours, erreur, raisonDeRefus, choisir, publier };
}

/* ------------------------------------------------------------------ */
/* Le corps : trois blocs plats                                        */
/* ------------------------------------------------------------------ */

/** Un bloc : un titre, un filet au-dessus (sauf le premier), son contenu. */
function Bloc({ titre, children }: { titre: string; children: ReactNode }): JSX.Element {
  return (
    <section aria-label={titre} className="border-t border-border-soft py-5 first:border-t-0 first:pt-0">
      <h3 className="mb-3 text-[0.8125rem] font-semibold text-ink-900">{titre}</h3>
      {children}
    </section>
  );
}

/** Un choix du prompt : son libelle, puis le menu du socle (`SettingsMenu`), sur une ligne. */
function ChoixDuPrompt({ libelle, value, options, onChange }: {
  libelle: string;
  value: string;
  options: Parameters<typeof SettingsMenu>[0]['options'];
  onChange(valeur: string): void;
}): JSX.Element {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <span className="text-sm text-ink-700">{libelle}</span>
      <SettingsMenu label={libelle} value={value} options={options} onChange={onChange} />
    </div>
  );
}

/** Le texte complet, replie sous son geste. */
function TexteReplie({ libelle, texte }: { libelle: string; texte: string }): JSX.Element {
  return (
    <details className="mt-3">
      <summary className="cursor-pointer text-xs text-ink-500">{libelle}</summary>
      <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-sm border border-border-soft p-3 font-mono text-xs text-ink-700">
        {texte}
      </pre>
    </details>
  );
}

/** Les deux choix de couleurs, dans l'ordre du menu. */
const CHOIX_DE_COULEURS: readonly { value: CouleursDeFichePerso; label: string }[] = [
  { value: 'environnement', label: 'Couleurs de l\'environnement' },
  { value: 'propres', label: 'Mes propres couleurs' },
];

/** Les deux choix de hauteur, dans l'ordre du menu. */
const CHOIX_DE_HAUTEUR: readonly { value: HauteurDeFichePerso; label: string }[] = [
  { value: 'defilement', label: 'Défilement' },
  { value: 'fixe', label: 'Fixe' },
];

/** Ce que l'atelier demande du depot d'un choix : `sdk.vuesHtml.couleurs` ou `.hauteur` (`@taki/sdk`), absent d'un SDK plus ancien. */
interface DepotDeChoix<T extends string> {
  lire(appId: string, porteId: string): Promise<T>;
  ecrire(appId: string, porteId: string, choix: T): Promise<void>;
}

/**
 * UN CHOIX de la page (couleurs, hauteur), une PREFERENCE de la personne (par
 * app et par porte) gardee par le SDK, `sdk.vuesHtml.<nom>`, dans UN document
 * que le serveur lit aussi (`preferencesVues`) : l'agent MCP recoit ainsi le
 * meme choix que le prompt copie (`vue_contrat`). Une aide d'ecriture, pas un
 * fait de la declaration, donc aucune revision au journal. Lu a l'ouverture (le
 * defaut pendant la lecture, et si elle echoue), ecrit au choix ; le choix vaut
 * aussitot a l'ecran, que l'ecriture reussisse ou non. Vaut pour le prompt ET
 * pour le contexte de l'agent.
 */
function useChoixDeFichePerso<T extends string>(
  fichePerso: ContexteDeFichePerso,
  nom: 'couleurs' | 'hauteur',
  defaut: T,
  lire: (valeur: unknown) => T,
): [T, (choix: T) => void] {
  const { sdk } = useAppRuntime();
  const depot = (sdk as unknown as { vuesHtml?: Partial<Record<'couleurs' | 'hauteur', DepotDeChoix<T>>> }).vuesHtml?.[nom];
  const appId = fichePerso.app.id;
  const porteId = fichePerso.porte.id;
  const [choix, setChoix] = useState<T>(defaut);
  /* Un choix fait pendant la lecture gagne sur elle. */
  const choisi = useRef(false);
  useEffect(() => {
    choisi.current = false;
    setChoix(defaut);
    if (depot === undefined) return;
    let vivant = true;
    depot.lire(appId, porteId).then(
      (lue) => { if (vivant && !choisi.current) setChoix(lire(lue)); },
      () => {},
    );
    return () => { vivant = false; };
  }, [depot, appId, porteId, defaut, lire]);
  const poser = useCallback(
    (valeur: T) => {
      choisi.current = true;
      setChoix(valeur);
      if (depot !== undefined) depot.ecrire(appId, porteId, valeur).catch(() => {});
    },
    [depot, appId, porteId],
  );
  return [choix, poser];
}

/** Le choix des couleurs de la page (`sdk.vuesHtml.couleurs`) ; l'environnement par defaut. */
export function useCouleursDeFichePerso(fichePerso: ContexteDeFichePerso): [CouleursDeFichePerso, (couleurs: CouleursDeFichePerso) => void] {
  return useChoixDeFichePerso(fichePerso, 'couleurs', COULEURS_DE_FICHE_PERSO_DEFAUT, couleursDeFichePerso);
}

/** Le choix de la hauteur de la page (`sdk.vuesHtml.hauteur`) ; le defilement par defaut. */
export function useHauteurDeFichePerso(fichePerso: ContexteDeFichePerso): [HauteurDeFichePerso, (hauteur: HauteurDeFichePerso) => void] {
  return useChoixDeFichePerso(fichePerso, 'hauteur', HAUTEUR_DE_FICHE_PERSO_DEFAUT, hauteurDeFichePerso);
}

/** Les deux choix de la page, tenus par le corps de l'atelier et lus par le prompt et par l'agent. */
interface ChoixDeLaPage {
  couleurs: CouleursDeFichePerso;
  hauteur: HauteurDeFichePerso;
}

/** Le bloc « Prompt IA » : les choix des couleurs et de la hauteur, puis copier le prompt fabrique des champs de la porte. */
function BlocPrompt({
  entree,
  choix,
  poserCouleurs,
  poserHauteur,
}: {
  entree: EntreeDeLAtelier;
  choix: ChoixDeLaPage;
  poserCouleurs(couleurs: CouleursDeFichePerso): void;
  poserHauteur(hauteur: HauteurDeFichePerso): void;
}): JSX.Element {
  const { fichePerso } = entree;
  const texte = useMemo(
    () =>
      promptDeFichePerso({
        porteLabel: fichePerso.porte.libelle,
        ...(fichePerso.porte.ficheSingulier === undefined ? {} : { ficheSingulier: fichePerso.porte.ficheSingulier }),
        champs: fichePerso.champs,
        liens: fichePerso.liens,
        couleurs: choix.couleurs,
        hauteur: choix.hauteur,
      }),
    [fichePerso, choix],
  );
  const { copie, copier: copierLeTexte } = useCopie();
  return (
    <Bloc titre="Prompt IA">
      {/* Deux choix, jamais une saisie de couleur ; aucune phrase d'aide (G11).
          UNE LIGNE, pas deux rangees du kit Parametres : dans la fenetre moyenne,
          deux rangees poussaient « Agent connecté » sous le pli (recette du 04/11). */}
      <div className="mb-3 flex flex-wrap items-center gap-x-6 gap-y-2">
        <ChoixDuPrompt libelle="Couleurs" value={choix.couleurs} options={CHOIX_DE_COULEURS} onChange={(valeur) => poserCouleurs(couleursDeFichePerso(valeur))} />
        <ChoixDuPrompt libelle="Hauteur" value={choix.hauteur} options={CHOIX_DE_HAUTEUR} onChange={(valeur) => poserHauteur(hauteurDeFichePerso(valeur))} />
      </div>
      <Button variant="secondary" size="sm" onClick={() => copierLeTexte(texte)}>
        {copie ? 'Copié' : 'Copier le prompt'}
      </Button>
      <TexteReplie libelle="Voir le prompt" texte={texte} />
    </Bloc>
  );
}

/** Le bloc « Fichier HTML » : choisir ou glisser, la lecture, l'apercu, l'echec. */
/**
 * LE BLOC « FICHIER HTML ». Dans la vue (`fenetre` faux), un fichier valide
 * s'affiche aussitot en apercu pleine largeur. Dans la FENETRE, jamais
 * d'apercu (retour de l'orchestrateur du 04/11 : « une petite fenetre au
 * milieu ») : le nom, « Fichier valide » ou l'erreur, « Remplacer le fichier » ;
 * le resultat se voit sur la fiche, derriere, des la publication.
 */
function BlocFichier({ entree, etat, fenetre }: { entree: EntreeDeLAtelier; etat: EtatDeLAtelier; fenetre: boolean }): JSX.Element {
  const { fichePerso, lecture } = entree;
  const champ = useRef<HTMLInputElement | null>(null);
  const [survol, setSurvol] = useState(false);
  const prendre = (fichiers: FileList | null | undefined): void => {
    const fichier = fichiers?.[0];
    if (fichier !== undefined) void etat.choisir(fichier);
  };
  const deposer = (evenement: DragEvent<HTMLDivElement>): void => {
    evenement.preventDefault();
    setSurvol(false);
    prendre(evenement.dataTransfer?.files);
  };
  /* « Remplacer » ouvre le selecteur sans rien effacer : le fichier et l'apercu actuels restent jusqu'au choix d'un nouveau. */
  const remplacer = (
    <Button variant="secondary" size="sm" onClick={() => champ.current?.click()}>Remplacer le fichier</Button>
  );
  const apercu = useMemo(
    () => (etat.page?.ok === true ? entree.donneesDe({ champs: etat.page.champs, liens: etat.page.liens }, lecture) : null),
    [etat.page, entree, lecture],
  );
  const libelleDeLaVue = fichePerso.publiee?.label ?? FICHE_PERSO_LIBELLE;
  return (
    <Bloc titre="Fichier HTML">
      <input
        ref={champ}
        type="file"
        accept=".html,text/html"
        className="sr-only"
        tabIndex={-1}
        aria-label="Fichier HTML"
        onChange={(evenement) => {
          prendre(evenement.target.files);
          evenement.target.value = '';
        }}
      />
      {etat.depot === null ? (
        <div
          onDragOver={(evenement) => { evenement.preventDefault(); setSurvol(true); }}
          onDragLeave={() => setSurvol(false)}
          onDrop={deposer}
          className={cn(
            'flex items-center gap-3 rounded-sm border border-dashed px-4 py-5',
            survol ? 'border-border-strong bg-surface-1' : 'border-border',
          )}
        >
          <Button variant="secondary" size="sm" onClick={() => champ.current?.click()}>
            Choisir un fichier
          </Button>
          <span className="text-xs text-ink-500">ou le glisser ici</span>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm text-ink-900">{etat.depot.nom}</p>
              {etat.page?.ok === true ? (
                <p className="text-xs text-ink-500">Fichier valide</p>
              ) : (
                <ul className="mt-1 text-xs text-danger" role="alert">
                  {(etat.page?.ok === false ? etat.page.erreurs : []).map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              )}
            </div>
            {etat.page?.ok === true && !fenetre ? null : remplacer}
          </div>
          {etat.page?.ok === true && !fenetre ? (
            <>
              <div className="flex items-center gap-3">
                <span className="min-w-0 flex-1 text-xs font-semibold text-ink-900">Aperçu non publié</span>
                {remplacer}
              </div>
              <div className="h-[28rem] w-full overflow-hidden rounded-sm border border-border" data-testid="apercu-fiche-perso">
                <VueHtmlIsolee html={etat.depot.html} donnees={apercu} titre={libelleDeLaVue} />
              </div>
            </>
          ) : null}
        </div>
      )}
      {etat.erreur === null ? null : (
        <p role="alert" className="mt-2 text-xs text-danger">{etat.erreur}</p>
      )}
    </Bloc>
  );
}

/** Le bloc « Agent connecté » : le contexte a coller, ou sa raison et le geste qui la leve. */
function BlocAgent({ entree, choix }: { entree: EntreeDeLAtelier; choix: ChoixDeLaPage }): JSX.Element {
  const { fichePerso, ficheId } = entree;
  const etat = useEtatDeLAgent();
  const { copie, copier: copierLeTexte } = useCopie();
  const texte = useMemo(
    () =>
      contexteMcpDeFichePerso({
        orgId: fichePerso.orgId,
        appId: fichePerso.app.id,
        door: fichePerso.porte.id,
        porteLabel: fichePerso.porte.libelle,
        ...(fichePerso.porte.ficheSingulier === undefined ? {} : { ficheSingulier: fichePerso.porte.ficheSingulier }),
        recordId: ficheId,
        viewId: idDeFichePerso(fichePerso.porte.id),
        label: fichePerso.publiee?.label ?? FICHE_PERSO_LIBELLE,
        champs: fichePerso.champs.map((champ) => champ.name),
        liens: fichePerso.liens.map((lien) => lien.id),
        couleurs: choix.couleurs,
        hauteur: choix.hauteur,
      }),
    [fichePerso, ficheId, choix],
  );
  return (
    <Bloc titre="Agent connecté">
      <div className="flex items-center gap-3">
        <CoqueDeRaison raison={etat.pret ? undefined : etat.raison}>
          <Button variant="secondary" size="sm" disabled={!etat.pret} onClick={() => copierLeTexte(texte)}>
            {copie ? 'Copié' : 'Copier le contexte'}
          </Button>
        </CoqueDeRaison>
        {etat.pret ? null : (
          <span className="text-xs text-ink-500" data-testid="raison-agent">
            {etat.raison}
            {/* Le geste n'est propose que pour une raison qui se regle aux Parametres. */}
            {etat.raison === RAISON_VERIFICATION ? null : (
              <>
                {' '}
                <a
                  href={ADRESSE_DES_PARAMETRES_IA}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-ink-700 underline"
                >
                  Ouvrir les Paramètres
                </a>
              </>
            )}
          </span>
        )}
      </div>
      {etat.pret ? <TexteReplie libelle="Voir le contexte" texte={texte} /> : null}
    </Bloc>
  );
}

/** Les trois blocs. En fenetre, l'etat « Vue publiée » et sa version viennent en tete. */
export function CorpsDeLAtelier({
  entree,
  etat,
  fenetre,
}: {
  entree: EntreeDeLAtelier;
  etat: EtatDeLAtelier;
  fenetre: boolean;
}): JSX.Element {
  const { publiee } = entree.fichePerso;
  const [couleurs, poserCouleurs] = useCouleursDeFichePerso(entree.fichePerso);
  const [hauteur, poserHauteur] = useHauteurDeFichePerso(entree.fichePerso);
  const choix = useMemo<ChoixDeLaPage>(() => ({ couleurs, hauteur }), [couleurs, hauteur]);
  return (
    <div className="flex flex-col" data-testid="atelier-fiche-perso">
      {fenetre && publiee !== null ? (
        <p className="mb-4 text-xs text-ink-500" data-testid="etat-vue-publiee">
          Vue publiée · version {publiee.version}
        </p>
      ) : null}
      <BlocPrompt entree={entree} choix={choix} poserCouleurs={poserCouleurs} poserHauteur={poserHauteur} />
      <BlocFichier entree={entree} etat={etat} fenetre={fenetre} />
      <BlocAgent entree={entree} choix={choix} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Les deux demeures de l'atelier                                      */
/* ------------------------------------------------------------------ */

/**
 * L'ATELIER DANS LA VUE (la fiche personnalisee pas encore publiee). « Publier »
 * est une action de la RANGEE 3, posee par `poserActions` (jamais un bouton dans
 * le corps), grisee avec sa raison tant qu'aucun fichier n'est valide (G16).
 */
export function AtelierEnLigne({
  entree,
  poserActions,
}: {
  entree: EntreeDeLAtelier;
  poserActions(actions: ActionsDeVueDeFiche | null): void;
}): JSX.Element {
  const etat = useAtelierDeFichePerso(entree.fichePerso);
  const { enCours, raisonDeRefus, publier } = etat;
  /* Rappele a chaque rendu : l'ecran ne se redessine que si l'affichage de l'action change. */
  useEffect(() => {
    poserActions({
      rangee: [
        {
          id: 'publier-fiche-perso',
          libelle: libelleDePublication(enCours),
          variante: 'primary',
          ...(raisonDeRefus === undefined ? {} : { desactivee: raisonDeRefus }),
          onClick: () => void publier(),
        },
      ],
    });
  });
  return <CorpsDeLAtelier entree={entree} etat={etat} fenetre={false} />;
}

/**
 * LA FENETRE « ÉDITER LA VUE CANVAS » : la `Modal` du socle (fond flouté), le
 * meme atelier, « Publier » au pied. Fermee par la croix, Échap ou le voile, sans
 * confirmation : le fichier choisi reste en memoire (il est tenu par l'ecran).
 * Une publication reussie la referme.
 */
export function FenetreDeFichePerso({
  entree,
  ouverte,
  onClose,
}: {
  entree: EntreeDeLAtelier;
  ouverte: boolean;
  onClose(): void;
}): JSX.Element {
  const etat = useAtelierDeFichePerso(entree.fichePerso, onClose);
  return (
    <Modal
      open={ouverte}
      onClose={onClose}
      title="Éditer la vue Canvas"
      size="lg"
      footer={
        <CoqueDeRaison raison={etat.raisonDeRefus}>
          <Button variant="primary" size="sm" disabled={etat.raisonDeRefus !== undefined} onClick={() => void etat.publier()}>
            {libelleDePublication(etat.enCours)}
          </Button>
        </CoqueDeRaison>
      }
    >
      <CorpsDeLAtelier entree={entree} etat={etat} fenetre />
    </Modal>
  );
}
